import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Page,
  Layout,
  Card,
  Button,
  Toast,
  BlockStack,
  InlineStack,
  Text,
  Box,
  Spinner,
  Badge,
  DataTable,
  EmptyState,
  Checkbox,
  Banner,
  Divider,
  TextField,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { useAuthenticatedFetch } from '../hooks/useApi';

interface DiscountCode {
  id: string;
  shopifyId: string;
  code: string;
  discountType: string;
  value: number;
  minimumPurchase?: number;
  usageLimit?: number;
  usageCount: number;
  isActive: boolean;
  startsAt?: string;
  endsAt?: string;
  showInChat: boolean;
  chatPriority: number;
  syncedAt: string;
}

export default function DiscountCodesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { shop } = useShop();
  const fetchWithAuth = useAuthenticatedFetch();

  const [discountCodes, setDiscountCodes] = useState<DiscountCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastError, setToastError] = useState(false);
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());

  // Load discount codes
  const loadDiscountCodes = useCallback(async () => {
    if (!shop) return;
    
    setIsLoading(true);
    try {
      const response = await fetchWithAuth('/api/discount-codes') as { discountCodes?: DiscountCode[] };
      setDiscountCodes(response.discountCodes || []);
    } catch (err) {
      console.error('Error loading discount codes:', err);
      showToast(t('discountCodes.loadError', 'Failed to load discount codes'), true);
    } finally {
      setIsLoading(false);
    }
  }, [shop, fetchWithAuth, t]);

  useEffect(() => {
    loadDiscountCodes();
  }, [loadDiscountCodes]);

  // Sync from Shopify
  const syncFromShopify = useCallback(async () => {
    if (!shop) return;
    
    setIsSyncing(true);
    try {
      const response = await fetchWithAuth('/api/discount-codes/sync', {
        method: 'POST'
      }) as { synced?: number };
      showToast(t('discountCodes.syncSuccess', 'Synced {{count}} discount codes from Shopify', { count: response.synced || 0 }));
      await loadDiscountCodes();
    } catch (err) {
      console.error('Error syncing discount codes:', err);
      showToast(t('discountCodes.syncError', 'Failed to sync from Shopify'), true);
    } finally {
      setIsSyncing(false);
    }
  }, [shop, fetchWithAuth, loadDiscountCodes, t]);

  // Toggle show in chat
  const toggleShowInChat = useCallback(async (discountId: string, showInChat: boolean, priority?: number) => {
    if (!shop) return;
    
    setUpdatingIds(prev => new Set([...prev, discountId]));
    try {
      await fetchWithAuth(`/api/discount-codes/${discountId}/chat-visibility`, {
        method: 'PUT',
        body: { showInChat, chatPriority: priority ?? 0 }
      });
      
      setDiscountCodes(prev => prev.map(dc => 
        dc.id === discountId 
          ? { ...dc, showInChat, chatPriority: priority ?? dc.chatPriority }
          : dc
      ));
      
      showToast(showInChat 
        ? t('discountCodes.enabledForChat', 'Discount will be shown in chat')
        : t('discountCodes.disabledForChat', 'Discount hidden from chat')
      );
    } catch (err) {
      console.error('Error updating discount visibility:', err);
      showToast(t('discountCodes.updateError', 'Failed to update'), true);
    } finally {
      setUpdatingIds(prev => {
        const next = new Set(prev);
        next.delete(discountId);
        return next;
      });
    }
  }, [shop, fetchWithAuth, t]);

  const showToast = (message: string, isError = false) => {
    setToastMessage(message);
    setToastError(isError);
    setToastActive(true);
  };

  const formatDiscountValue = (dc: DiscountCode) => {
    switch (dc.discountType) {
      case 'percentage':
        return `${dc.value}%`;
      case 'fixed_amount':
        return `${dc.value}€`;
      case 'free_shipping':
        return t('discountCodes.freeShipping', 'Free shipping');
      default:
        return `${dc.value}`;
    }
  };

  const getDiscountTypeBadge = (type: string) => {
    switch (type) {
      case 'percentage':
        return <Badge tone="success">{t('discountCodes.percentage', 'Percentage')}</Badge>;
      case 'fixed_amount':
        return <Badge tone="info">{t('discountCodes.fixedAmount', 'Fixed Amount')}</Badge>;
      case 'free_shipping':
        return <Badge>{t('discountCodes.freeShipping', 'Free Shipping')}</Badge>;
      default:
        return <Badge>{type}</Badge>;
    }
  };

  const getStatusBadge = (dc: DiscountCode) => {
    if (!dc.isActive) {
      return <Badge tone="critical">{t('discountCodes.inactive', 'Inactive')}</Badge>;
    }
    if (dc.endsAt && new Date(dc.endsAt) < new Date()) {
      return <Badge tone="warning">{t('discountCodes.expired', 'Expired')}</Badge>;
    }
    if (dc.usageLimit && dc.usageCount >= dc.usageLimit) {
      return <Badge tone="warning">{t('discountCodes.usedUp', 'Used up')}</Badge>;
    }
    return <Badge tone="success">{t('discountCodes.active', 'Active')}</Badge>;
  };

  if (isLoading) {
    return (
      <Page title={t('discountCodes.title', 'Discount Codes')}>
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="800">
                <InlineStack gap="200" blockAlign="center" align="center">
                  <Spinner size="small" />
                  <Text as="p" variant="bodyMd">{t('common.loading')}</Text>
                </InlineStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const chatEnabledCount = discountCodes.filter(dc => dc.showInChat).length;

  return (
    <Page
      title={t('discountCodes.title', 'Discount Codes')}
      subtitle={t('discountCodes.subtitle', 'Manage which discount codes appear in chat recommendations')}
      backAction={{ content: t('common.back'), onAction: () => navigate('/app/chat/settings') }}
      primaryAction={{
        content: t('discountCodes.syncFromShopify', 'Sync from Shopify'),
        onAction: syncFromShopify,
        loading: isSyncing
      }}
    >
        <Layout>
          <Layout.Section>
            <Banner tone="info">
              <Text as="p" variant="bodyMd">
                {t('discountCodes.info', 'Select which discount codes you want the AI chat assistant to recommend to customers. The AI will mention these codes when relevant to help increase conversions.')}
              </Text>
            </Banner>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center">
                  <BlockStack gap="100">
                    <Text as="h2" variant="headingMd">
                      {t('discountCodes.yourCodes', 'Your Discount Codes')}
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      {t('discountCodes.chatEnabledCount', '{{count}} codes enabled for chat', { count: chatEnabledCount })}
                    </Text>
                  </BlockStack>
                  <Button onClick={syncFromShopify} loading={isSyncing}>
                    {t('discountCodes.refresh', 'Refresh')}
                  </Button>
                </InlineStack>

                <Divider />

                {discountCodes.length === 0 ? (
                  <EmptyState
                    heading={t('discountCodes.noCodesTitle', 'No discount codes found')}
                    action={{
                      content: t('discountCodes.syncFromShopify', 'Sync from Shopify'),
                      onAction: syncFromShopify,
                      loading: isSyncing
                    }}
                    image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  >
                    <Text as="p" variant="bodyMd">
                      {t('discountCodes.noCodesDescription', 'Create discount codes in your Shopify admin, then sync them here to enable for chat.')}
                    </Text>
                  </EmptyState>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
                    headings={[
                      t('discountCodes.code', 'Code'),
                      t('discountCodes.type', 'Type'),
                      t('discountCodes.value', 'Value'),
                      t('discountCodes.status', 'Status'),
                      t('discountCodes.usage', 'Usage'),
                      t('discountCodes.showInChat', 'Show in Chat'),
                      t('discountCodes.priority', 'Priority'),
                    ]}
                    rows={discountCodes.map(dc => [
                      <Text as="span" variant="bodyMd" fontWeight="bold">
                        <code style={{ 
                          backgroundColor: '#f4f6f8', 
                          padding: '4px 8px', 
                          borderRadius: '4px',
                          fontSize: '13px'
                        }}>
                          {dc.code}
                        </code>
                      </Text>,
                      getDiscountTypeBadge(dc.discountType),
                      <Text as="span" variant="bodyMd" fontWeight="semibold" tone="success">
                        {formatDiscountValue(dc)}
                      </Text>,
                      getStatusBadge(dc),
                      <Text as="span" variant="bodySm" tone="subdued">
                        {dc.usageLimit 
                          ? `${dc.usageCount}/${dc.usageLimit}`
                          : dc.usageCount.toString()}
                      </Text>,
                      updatingIds.has(dc.id) ? (
                        <Spinner size="small" />
                      ) : (
                        <Checkbox
                          label=""
                          labelHidden
                          checked={dc.showInChat}
                          onChange={() => toggleShowInChat(dc.id, !dc.showInChat)}
                          disabled={!dc.isActive}
                        />
                      ),
                      dc.showInChat ? (
                        <TextField
                          label=""
                          labelHidden
                          type="number"
                          value={dc.chatPriority.toString()}
                          onChange={(val) => toggleShowInChat(dc.id, true, parseInt(val) || 0)}
                          autoComplete="off"
                          min={0}
                          max={100}
                          disabled={updatingIds.has(dc.id)}
                        />
                      ) : (
                        <Text as="span" variant="bodySm" tone="subdued">-</Text>
                      ),
                    ])}
                  />
                )}
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  💡 {t('discountCodes.tipsTitle', 'Tips for Chat Discounts')}
                </Text>
                <BlockStack gap="200">
                  <Text as="p" variant="bodyMd">
                    • {t('discountCodes.tip1', 'Enable 2-3 active discount codes for best results')}
                  </Text>
                  <Text as="p" variant="bodyMd">
                    • {t('discountCodes.tip2', 'Use higher priority (1-100) for discounts you want mentioned first')}
                  </Text>
                  <Text as="p" variant="bodyMd">
                    • {t('discountCodes.tip3', 'The AI will naturally mention discounts when customers ask about prices or deals')}
                  </Text>
                  <Text as="p" variant="bodyMd">
                    • {t('discountCodes.tip4', 'Expired or inactive codes are automatically hidden from chat')}
                  </Text>
                </BlockStack>
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>

      {toastActive && (
        <Toast
          content={toastMessage}
          error={toastError}
          onDismiss={() => setToastActive(false)}
        />
      )}
    </Page>
  );
}
